import React from 'react';
import { ControllerRenderProps } from 'react-hook-form';

import DropdownSmall from '@rover/icons/svg/dropdown-small.svg';
import { Button, Flex, Text } from '@rover/kibble/core';
import BottomSheet from '@rover/kibble/official-patterns/BottomSheet';
import useTheme from '@rover/kibble/themes/useTheme';

import { TrainingNeedsPills } from '../../../components/TrainingNeedsPills/TrainingNeedsPills';

import { Schema } from './needsInputSchema';

type MobileTrainingNeedsInputProps = {
  selectedSkills: string[];
  selectedBehaviors: string[];
  behaviorsField: ControllerRenderProps<Schema, 'behaviors'>;
  skillsField: ControllerRenderProps<Schema, 'skills'>;
  submitButtonProps: {
    type: 'submit';
    onPress: (events: { webEvent?: React.MouseEvent<HTMLElement>; rxnEvent?: unknown }) => unknown;
    disabled?: boolean;
    loading?: boolean;
  };
  showBottomSheet: boolean;
  setShowBottomSheet: React.Dispatch<React.SetStateAction<boolean>>;
};

export const MobileTrainingNeedsInput = ({
  selectedBehaviors,
  selectedSkills,
  behaviorsField,
  skillsField,
  submitButtonProps,
  showBottomSheet,
  setShowBottomSheet,
}: MobileTrainingNeedsInputProps): JSX.Element => {
  const theme = useTheme();
  const selectedCount = selectedBehaviors.length + selectedSkills.length;

  const itemsSelected = selectedCount > 0;

  const selectedCountText = itemsSelected ? ` (${selectedCount})` : '';

  const buttonBorderColor = itemsSelected
    ? theme.colors.interactive_text.primary_active
    : theme.colors.interactive_border.primary_base;

  const onCloseBottomSheet = (): void => {
    setShowBottomSheet(false);
    behaviorsField.onChange(selectedBehaviors);
    skillsField.onChange(selectedSkills);
  };

  return (
    <>
      <Button
        sx={{
          borderRadius: '8px',
          border: `2px solid ${buttonBorderColor}`,
          backgroundColor: itemsSelected
            ? theme.background.accent_secondary
            : theme.background.primary,
        }}
        px="3x"
        size="small"
        onPress={() => setShowBottomSheet(true)}
        data-testid="needs-bottom-sheet-open"
      >
        <Flex alignItems="center" gap="2x">
          <Text size="100">Training needs {selectedCountText}</Text>
          <DropdownSmall width="16px" height="16px" />
        </Flex>
      </Button>
      <BottomSheet isOpen={showBottomSheet} onRequestClose={onCloseBottomSheet} isFullHeight>
        <TrainingNeedsPills
          title="What training needs do you have?"
          subtitle="We'll show trainers with relevant experience."
          selectedSkills={skillsField.value}
          selectedBehaviors={behaviorsField.value}
          behaviorsOnChange={behaviorsField.onChange}
          skillsOnChange={skillsField.onChange}
        />
        <Button {...submitButtonProps} fullWidth variant="primary" size="small" mt="4x">
          Update search
        </Button>
      </BottomSheet>
    </>
  );
};
